import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { PrivateChatContext } from "../chat/PrivateChatContext";
import { useSidebarContext } from "../SidebarContext";
import PropTypes from "prop-types";

const SocialContext = createContext();

const PARTICIPANT_TOGGLER_ID = "participants";
const CHAT_TOGGLER_ID = "chat";
const USER_SETTINGS_TOGGLER_ID = "user-settings";

const validSidebarIds = [PARTICIPANT_TOGGLER_ID, CHAT_TOGGLER_ID, USER_SETTINGS_TOGGLER_ID];

const isValidSidebar = sidebarId => {
  return validSidebarIds.includes(sidebarId);
};

const useSocialSidebar = () => {
  const { sidebarId, setSidebar } = useSidebarContext();

  // Split the sidebarId into an array of active sidebars
  const activeSidebars = useMemo(() => {
    return sidebarId ? sidebarId.split(".") : [];
  }, [sidebarId]);

  // Determine which sidebars are open
  const isOpen = useMemo(
    () => ({
      participants: activeSidebars.includes(PARTICIPANT_TOGGLER_ID),
      chat: activeSidebars.includes(CHAT_TOGGLER_ID),
      userSettings: activeSidebars.includes(USER_SETTINGS_TOGGLER_ID)
    }),
    [activeSidebars]
  );

  // Generic function to toggle sidebars
  const toggleSidebar = useCallback(
    (sidebar, value) => {
      const updatedSidebars = new Set(activeSidebars);

      const sidebarsAreValid = activeSidebars.every(activeSidebar => isValidSidebar(activeSidebar));

      if (!sidebarsAreValid) {
        updatedSidebars.clear();
      }

      // if (sidebar === PARTICIPANT_TOGGLER_ID && !value) {
      //   setSidebar(null);
      //   return;
      // }

      if (value) {
        updatedSidebars.add(sidebar);
      } else {
        updatedSidebars.delete(sidebar);
      }

      // Convert the Set back to a sorted array to maintain consistency
      const newSidebarId = Array.from(updatedSidebars).sort().join(".");

      setSidebar(newSidebarId);
    },
    [activeSidebars, setSidebar]
  );

  // Handlers for each sidebar
  const setIsOpenParticipants = useCallback(value => toggleSidebar("participants", value), [toggleSidebar]);
  const setIsOpenChat = useCallback(value => toggleSidebar("chat", value), [toggleSidebar]);
  const setIsOpenSettings = useCallback(value => toggleSidebar("user-settings", value), [toggleSidebar]);

  return {
    isOpenParticipants: isOpen.participants,
    isOpenChat: isOpen.chat,
    isOpenSettings: isOpen.userSettings,
    setIsOpenParticipants,
    setIsOpenChat,
    setIsOpenSettings
  };
};

export const useSocialContext = () => {
  const context = useContext(SocialContext);
  if (!context) {
    throw new Error("useSocialContext must be used within a SocialProvider");
  }
  return context;
};

export const SocialProvider = ({ children, people, mySessionId }) => {
  const { privateMessageGroups, readAllMesages } = useContext(PrivateChatContext);

  const myMessages = privateMessageGroups.filter(group => {
    if (group.receiver === mySessionId || group.senderSessionId === mySessionId) {
      return group;
    }
  });

  // List of all currently present user IDs
  const onlineUserIds = people.map(person => person.id);

  // Filter myMessages to only include conversations with present users
  const filteredMyMessages = myMessages.filter(group => {
    const otherUserId = group.senderSessionId === mySessionId ? group.receiver : group.senderSessionId;
    return onlineUserIds.includes(otherUserId);
  });

  // Recalculate hasUnreadMessages to consider only present users
  const hasUnreadMessages = filteredMyMessages.some(group => group.messages.some(msg => !msg.read));

  useEffect(() => {
    // When people list changes, mark all messages from users who are no longer present as read
    const messagesFromAbsentUsers = myMessages.filter(group => {
      const otherUserId = group.senderSessionId === mySessionId ? group.receiver : group.senderSessionId;
      return !onlineUserIds.includes(otherUserId) && group.messages.some(msg => !msg.read);
    });

    messagesFromAbsentUsers.forEach(group => {
      // Mark all messages from this user as read
      const otherUserId = group.senderSessionId === mySessionId ? group.receiver : group.senderSessionId;
      readAllMesages(otherUserId);
    });
  }, [people, myMessages, readAllMesages, mySessionId, onlineUserIds]);

  const { isOpenParticipants, setIsOpenParticipants, isOpenChat, setIsOpenChat, isOpenSettings, setIsOpenSettings } =
    useSocialSidebar();
  const [selectedUserChat, setUserChat] = useState(null);
  const [lastConversationWith, setLastConversationWith] = useState(null);

  const personLoggedOut =
    selectedUserChat && !selectedUserChat.isAi && !people.find(person => person.id === selectedUserChat.id);

  const openChatUser = user => {
    setIsOpenChat(true);
    if (!user) {
      user = lastConversationWith;
    } else {
      setLastConversationWith(user);
    }
    setUserChat(user);

    if (!user?.isAi) {
      readAllMesages(user?.id);
    }
  };

  const toggleParticipants = async () => {
    setIsOpenParticipants(!isOpenParticipants);
  };

  const onOpenSettings = value => {
    setIsOpenSettings(value);
  };

  const toggleChat = () => {
    const open = !isOpenChat;
    if (open) {
      setUserChat(lastConversationWith);
    }
    setIsOpenChat(open);
  };

  const closeChat = ({ clearUsers }) => {
    setIsOpenChat(false);
    if (clearUsers) {
      setUserChat(null);
      setLastConversationWith(null);
    }
  };

  const openChatUserAll = () => {
    setIsOpenChat(true);
    setUserChat(null);
    setLastConversationWith(null);
  };

  const closeParticipants = () => {
    setIsOpenParticipants(false);
  };

  useEffect(() => {
    if (personLoggedOut && selectedUserChat) {
      readAllMesages(selectedUserChat.id);

      setIsOpenChat(false);
      setLastConversationWith(null);
    }
  }, [personLoggedOut, readAllMesages, selectedUserChat, setIsOpenChat]);

  const getLatestPrivateMessage = otherUserId => {
    let latestMessage = null;

    myMessages.forEach(msg => {
      const isCurrentConversation =
        (msg.senderSessionId === mySessionId && msg.receiver === otherUserId) ||
        (msg.senderSessionId === otherUserId && msg.receiver === mySessionId);

      if (isCurrentConversation) {
        if (!latestMessage || msg.timestamp > latestMessage.timestamp) {
          latestMessage = msg;
        }
      }
    });
    return latestMessage;
  };

  const getUnreadMessagesCount = useCallback(
    otherUserId => {
      const isUserLoggedIn = people.some(person => person.id === otherUserId);
      if (!isUserLoggedIn) {
        return "";
      }

      let unreadMessagesCount = 0;

      myMessages.forEach(group => {
        if (group.receiver === mySessionId && group.senderSessionId === otherUserId) {
          group.messages.forEach(msg => {
            if (!msg.read) {
              unreadMessagesCount++;
            }
          });
        }
      });

      return unreadMessagesCount > 0 ? unreadMessagesCount : "";
    },
    [people, myMessages, mySessionId]
  );

  return (
    <SocialContext.Provider
      value={{
        isOpenParticipants,
        isOpenSettings,
        isOpenChat,
        selectedUserChat,
        openChatUser,
        openChatUserAll,
        toggleParticipants,
        onOpenSettings,
        toggleChat,
        closeParticipants,
        closeChat,
        setIsOpenSettings,
        myMessages,
        getLatestPrivateMessage,
        getUnreadMessagesCount,
        readAllMesages,
        privateUnreadMessages: hasUnreadMessages
      }}
    >
      {children}
    </SocialContext.Provider>
  );
};

SocialProvider.propTypes = {
  children: PropTypes.node.isRequired,
  people: PropTypes.array.isRequired,
  mySessionId: PropTypes.string.isRequired
};
